import React from 'react';
import Icon from '../../../components/AppIcon';

const GenerationStats = ({ 
  totalGenerated = 0, 
  todayGenerated = 0, 
  savedPrompts = 0,
  userTier = 'free' 
}) => {
  const getTierLimits = (tier) => {
    const limits = {
      free: { daily: 5, monthly: 50, saves: 0 },
      launch: { daily: 25, monthly: 250, saves: 50 },
      scaleup: { daily: 100, monthly: 1000, saves: 500 },
      command: { daily: -1, monthly: -1, saves: -1 }
    };
    return limits?.[tier] || limits?.free;
  };

  const limits = getTierLimits(userTier);
  const dailyProgress = limits?.daily === -1 ? 100 : (todayGenerated / limits?.daily) * 100;
  const monthlyProgress = limits?.monthly === -1 ? 100 : (totalGenerated / limits?.monthly) * 100;

  const stats = [
    {
      label: 'Today',
      value: todayGenerated,
      limit: limits?.daily,
      progress: dailyProgress,
      icon: 'Calendar',
      color: 'text-blue-600'
    },
    {
      label: 'This Month',
      value: totalGenerated,
      limit: limits?.monthly,
      progress: monthlyProgress,
      icon: 'BarChart3',
      color: 'text-green-600'
    },
    {
      label: 'Saved',
      value: savedPrompts,
      limit: limits?.saves,
      progress: limits?.saves === -1 ? 100 : (savedPrompts / limits?.saves) * 100,
      icon: 'Bookmark',
      color: 'text-purple-600'
    }
  ];

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">
            Generation Statistics
          </h3>
          <div className="flex items-center space-x-2 text-sm">
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              userTier === 'command' ? 'bg-purple-100 text-purple-700' :
              userTier === 'scaleup' ? 'bg-blue-100 text-blue-700' :
              userTier === 'launch'? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {userTier?.charAt(0)?.toUpperCase() + userTier?.slice(1)} Plan
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center p-4 bg-muted/30 rounded-lg">
              <div className={`w-10 h-10 mx-auto mb-3 rounded-full bg-white flex items-center justify-center ${stat?.color}`}>
                <Icon name={stat?.icon} size={20} />
              </div>
              
              <div className="space-y-2">
                <div className="text-2xl font-bold text-text-primary">
                  {stat?.value}
                  {stat?.limit !== -1 && (
                    <span className="text-sm font-normal text-text-secondary">
                      /{stat?.limit}
                    </span>
                  )}
                </div>
                
                <div className="text-sm text-text-secondary">
                  {stat?.label}
                </div>
                
                {stat?.limit !== -1 && (
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-smooth ${
                        stat?.progress >= 90 ? 'bg-red-500' :
                        stat?.progress >= 70 ? 'bg-yellow-500': 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(stat?.progress, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {(dailyProgress >= 80 || monthlyProgress >= 80) && userTier !== 'command' && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertTriangle" size={16} />
              <span className="text-sm font-medium">
                You're approaching your {dailyProgress >= 80 ? 'daily' : 'monthly'} limit
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationStats;